import { useEffect } from 'react';

interface SeoHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object | object[];
}

const DEFAULT_TITLE = 'Van Polat Tesisat | Su Kaçağı Tespiti, Tıkanıklık Açma, Kombi Servisi Van';
const DEFAULT_OG_IMAGE = '/og-image.jpg';

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
}: SeoHeadProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz';

    // Title
    if (title) document.title = title;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, true);
      setMeta('twitter:description', description);
    }
    if (keywords) setMeta('keywords', keywords);
    if (title) {
      setMeta('og:title', title, true);
      setMeta('twitter:title', title);
    }

    // OG / Twitter image
    const resolvedImage = ogImage
      ? (ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`)
      : `${siteUrl}${DEFAULT_OG_IMAGE}`;
    setMeta('og:image', resolvedImage, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:image', resolvedImage);
    setMeta('twitter:card', 'summary_large_image');

    // Canonical + og:url
    const resolvedCanonical = canonical
      ? (canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`)
      : siteUrl;
    setMeta('og:url', resolvedCanonical, true);

    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', resolvedCanonical);

    // Schema JSON-LD
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      const existing = document.querySelectorAll('script[type="application/ld+json"][data-page]');
      existing.forEach((el) => el.remove());

      schemas.forEach((s, i) => {
        const scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.setAttribute('data-page', String(i));
        scriptEl.textContent = JSON.stringify(s);
        document.head.appendChild(scriptEl);
      });
    }

    return () => {
      // Restore default title on unmount
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, keywords, canonical, ogImage, schema]);

  return null;
}
